import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import {
  LogOut, BarChart3, Users, Gift, Store, MessageSquareText,
  Settings as SetIcon, Plus, Trash2, Save, Download,
} from "lucide-react";

const TABS = [
  { key: "stats", label: "Statistiche", icon: BarChart3 },
  { key: "bookmakers", label: "Bookmaker", icon: Store },
  { key: "promotions", label: "Promozioni", icon: Gift },
  { key: "applications", label: "Candidature", icon: Users },
  { key: "contacts", label: "Contatti", icon: MessageSquareText },
  { key: "settings", label: "Impostazioni", icon: SetIcon },
];

const emptyBM = { name: "", slug: "", logo_url: "", accent_emoji: "", short_description: "", promo_title: "", promo_description: "", conditions: [], signup_url: "", active: true, featured: false, is_new: false, order: 0 };
const emptyPromo = { bookmaker_slug: "", title: "", description: "", max_amount: "", category: "sport", valid_until: "", conditions: [], signup_url: "", active: true, is_new: false, featured: false };

const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30 focus:outline-none px-3 py-2 text-sm text-white";

function toCSV(rows, cols) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map(esc).join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}
function download(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState({ clicks: {}, applications_count: 0, contacts_count: 0 });
  const [bms, setBMs] = useState([]);
  const [promos, setPromos] = useState([]);
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [set, setSet] = useState(settings);
  const [editingBM, setEditingBM] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);

  const load = async () => {
    try {
      const [s, b, p, a, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/bookmakers"),
        api.get("/admin/promotions"), api.get("/admin/applications"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data); setBMs(b.data); setPromos(p.data);
      setApps(a.data); setContacts(c.data);
    } catch (e) { toast.error("Impossibile caricare i dati"); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setSet(settings); }, [settings]);

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const saveBM = async () => {
    try {
      const payload = { ...editingBM, conditions: (editingBM.conditions_text || editingBM.conditions.join("\n") || "").split("\n").filter(Boolean), order: Number(editingBM.order) || 0 };
      delete payload.conditions_text;
      if (editingBM.id) await api.put(`/admin/bookmakers/${editingBM.id}`, payload);
      else await api.post("/admin/bookmakers", payload);
      toast.success("Bookmaker salvato"); setEditingBM(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delBM = async (id) => {
    if (!window.confirm("Eliminare questo bookmaker?")) return;
    await api.delete(`/admin/bookmakers/${id}`); toast.success("Eliminato"); load();
  };

  const savePromo = async () => {
    try {
      const payload = { ...editingPromo, conditions: (editingPromo.conditions_text || editingPromo.conditions.join("\n") || "").split("\n").filter(Boolean) };
      delete payload.conditions_text;
      if (editingPromo.id) await api.put(`/admin/promotions/${editingPromo.id}`, payload);
      else await api.post("/admin/promotions", payload);
      toast.success("Promozione salvata"); setEditingPromo(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delPromo = async (id) => {
    if (!window.confirm("Eliminare questa promozione?")) return;
    await api.delete(`/admin/promotions/${id}`); toast.success("Eliminata"); load();
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", set);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/5 sticky top-0 bg-black/70 backdrop-blur-xl z-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={settings.logo_url} alt="VINKO" className="h-9 w-auto" />
            <span className="text-xs font-bold uppercase tracking-widest text-yellow-500">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-zinc-400">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-yellow-400 hover:text-yellow-400 text-sm px-4 py-2 transition-colors duration-200">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pb-2 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map(({ key, label, icon: Ic }) => (
            <button
              key={key} onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors duration-200 ${
                tab === key ? "bg-yellow-400 text-black border-yellow-400" : "border-white/10 text-zinc-300 hover:border-yellow-400/40"
              }`}
            >
              <Ic className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        {tab === "stats" && (
          <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Candidature", value: stats.applications_count },
              { label: "Contatti", value: stats.contacts_count },
              ...Object.entries(stats.clicks || {}).map(([k, v]) => ({ label: `Click ${k}`, value: v })),
            ].map((c) => (
              <div key={c.label} className="rounded-2xl border border-white/10 bg-[#101010] p-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500">{c.label}</div>
                <div className="mt-2 text-3xl font-black text-yellow-400 tracking-tighter">{c.value ?? 0}</div>
              </div>
            ))}
          </section>
        )}

        {tab === "bookmakers" && (
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold tracking-tight">Bookmaker</h2>
              <button onClick={() => setEditingBM({ ...emptyBM })} data-testid="admin-new-bm" className="inline-flex items-center gap-2 rounded-full bg-yellow-400 text-black font-bold text-sm px-4 py-2">
                <Plus className="w-4 h-4" /> Nuovo
              </button>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#101010] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-zinc-400 text-xs uppercase tracking-widest">
                  <tr className="border-b border-white/5"><th className="p-4">Nome</th><th className="p-4">Slug</th><th className="p-4">Attivo</th><th className="p-4">In evidenza</th><th className="p-4"></th></tr>
                </thead>
                <tbody>
                  {bms.map((b) => (
                    <tr key={b.id} className="border-b border-white/5">
                      <td className="p-4 font-semibold">{b.name}</td>
                      <td className="p-4 text-zinc-400">{b.slug}</td>
                      <td className="p-4">{b.active ? "✅" : "—"}</td>
                      <td className="p-4">{b.featured ? "⭐" : "—"}</td>
                      <td className="p-4 text-right space-x-2">
                        <button onClick={() => setEditingBM({ ...b, conditions_text: (b.conditions || []).join("\n") })} className="text-yellow-400 hover:text-yellow-300 text-xs font-semibold">Modifica</button>
                        <button onClick={() => delBM(b.id)} className="text-red-400 hover:text-red-300 text-xs font-semibold"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editingBM && (
              <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4">
                <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 max-h-[90vh] overflow-y-auto">
                  <h3 className="text-lg font-bold mb-4">{editingBM.id ? "Modifica" : "Nuovo"} Bookmaker</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="text-xs text-zinc-400">Nome<input className={inputCls} value={editingBM.name} onChange={(e) => setEditingBM({ ...editingBM, name: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Slug<input className={inputCls} value={editingBM.slug} onChange={(e) => setEditingBM({ ...editingBM, slug: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Logo URL<input className={inputCls} value={editingBM.logo_url} onChange={(e) => setEditingBM({ ...editingBM, logo_url: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Emoji<input className={inputCls} value={editingBM.accent_emoji} onChange={(e) => setEditingBM({ ...editingBM, accent_emoji: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400 md:col-span-2">Descrizione breve<textarea rows={2} className={inputCls} value={editingBM.short_description} onChange={(e) => setEditingBM({ ...editingBM, short_description: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Titolo promo<input className={inputCls} value={editingBM.promo_title} onChange={(e) => setEditingBM({ ...editingBM, promo_title: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Descrizione promo<input className={inputCls} value={editingBM.promo_description} onChange={(e) => setEditingBM({ ...editingBM, promo_description: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400 md:col-span-2">Condizioni (una per riga)<textarea rows={4} className={inputCls} value={editingBM.conditions_text ?? (editingBM.conditions || []).join("\n")} onChange={(e) => setEditingBM({ ...editingBM, conditions_text: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400 md:col-span-2">URL registrazione<input className={inputCls} value={editingBM.signup_url} onChange={(e) => setEditingBM({ ...editingBM, signup_url: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Ordine<input type="number" className={inputCls} value={editingBM.order} onChange={(e) => setEditingBM({ ...editingBM, order: e.target.value })} /></label>
                    <div className="flex items-center gap-4">
                      <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={editingBM.active} onChange={(e) => setEditingBM({ ...editingBM, active: e.target.checked })} /> Attivo</label>
                      <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={editingBM.featured} onChange={(e) => setEditingBM({ ...editingBM, featured: e.target.checked })} /> In evidenza</label>
                      <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={editingBM.is_new} onChange={(e) => setEditingBM({ ...editingBM, is_new: e.target.checked })} /> Nuova</label>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <button onClick={() => setEditingBM(null)} className="rounded-full border border-white/15 px-4 py-2 text-sm">Annulla</button>
                    <button onClick={saveBM} className="rounded-full bg-yellow-400 text-black font-bold px-4 py-2 text-sm inline-flex items-center gap-2"><Save className="w-4 h-4" /> Salva</button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {tab === "promotions" && (
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold tracking-tight">Promozioni</h2>
              <button onClick={() => setEditingPromo({ ...emptyPromo })} className="inline-flex items-center gap-2 rounded-full bg-yellow-400 text-black font-bold text-sm px-4 py-2"><Plus className="w-4 h-4" /> Nuova</button>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#101010] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-zinc-400 text-xs uppercase tracking-widest">
                  <tr className="border-b border-white/5"><th className="p-4">Titolo</th><th className="p-4">Bookmaker</th><th className="p-4">Categoria</th><th className="p-4">Attiva</th><th className="p-4"></th></tr>
                </thead>
                <tbody>
                  {promos.map((p) => (
                    <tr key={p.id} className="border-b border-white/5">
                      <td className="p-4 font-semibold">{p.title}</td>
                      <td className="p-4 text-zinc-400">{p.bookmaker_slug}</td>
                      <td className="p-4 text-zinc-400">{p.category}</td>
                      <td className="p-4">{p.active ? "✅" : "—"}</td>
                      <td className="p-4 text-right space-x-2">
                        <button onClick={() => setEditingPromo({ ...p, conditions_text: (p.conditions || []).join("\n") })} className="text-yellow-400 text-xs font-semibold">Modifica</button>
                        <button onClick={() => delPromo(p.id)} className="text-red-400 text-xs font-semibold"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editingPromo && (
              <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4">
                <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 max-h-[90vh] overflow-y-auto">
                  <h3 className="text-lg font-bold mb-4">{editingPromo.id ? "Modifica" : "Nuova"} Promozione</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="text-xs text-zinc-400">Bookmaker slug
                      <select className={inputCls} value={editingPromo.bookmaker_slug} onChange={(e) => setEditingPromo({ ...editingPromo, bookmaker_slug: e.target.value })}>
                        <option value="">-- seleziona --</option>
                        {bms.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                      </select>
                    </label>
                    <label className="text-xs text-zinc-400">Categoria
                      <select className={inputCls} value={editingPromo.category} onChange={(e) => setEditingPromo({ ...editingPromo, category: e.target.value })}>
                        <option value="sport">Sport</option><option value="casino">Casinò</option><option value="virtual">Virtual</option><option value="slot">Slot &amp; Games</option>
                      </select>
                    </label>
                    <label className="text-xs text-zinc-400 md:col-span-2">Titolo<input className={inputCls} value={editingPromo.title} onChange={(e) => setEditingPromo({ ...editingPromo, title: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400 md:col-span-2">Descrizione<textarea rows={2} className={inputCls} value={editingPromo.description} onChange={(e) => setEditingPromo({ ...editingPromo, description: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Importo massimo<input className={inputCls} value={editingPromo.max_amount} onChange={(e) => setEditingPromo({ ...editingPromo, max_amount: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400">Valida fino al<input className={inputCls} value={editingPromo.valid_until || ""} onChange={(e) => setEditingPromo({ ...editingPromo, valid_until: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400 md:col-span-2">Condizioni (una per riga)<textarea rows={4} className={inputCls} value={editingPromo.conditions_text ?? (editingPromo.conditions || []).join("\n")} onChange={(e) => setEditingPromo({ ...editingPromo, conditions_text: e.target.value })} /></label>
                    <label className="text-xs text-zinc-400 md:col-span-2">URL registrazione<input className={inputCls} value={editingPromo.signup_url} onChange={(e) => setEditingPromo({ ...editingPromo, signup_url: e.target.value })} /></label>
                    <div className="flex items-center gap-4 md:col-span-2">
                      <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={editingPromo.active} onChange={(e) => setEditingPromo({ ...editingPromo, active: e.target.checked })} /> Attiva</label>
                      <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={editingPromo.featured} onChange={(e) => setEditingPromo({ ...editingPromo, featured: e.target.checked })} /> In evidenza</label>
                      <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={editingPromo.is_new} onChange={(e) => setEditingPromo({ ...editingPromo, is_new: e.target.checked })} /> Nuova</label>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <button onClick={() => setEditingPromo(null)} className="rounded-full border border-white/15 px-4 py-2 text-sm">Annulla</button>
                    <button onClick={savePromo} className="rounded-full bg-yellow-400 text-black font-bold px-4 py-2 text-sm inline-flex items-center gap-2"><Save className="w-4 h-4" /> Salva</button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {tab === "applications" && (
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold tracking-tight">Candidature ({apps.length})</h2>
              <button
                onClick={() => download("candidature.csv", toCSV(apps, ["created_at", "first_name", "last_name", "email", "phone", "city", "province", "application_type", "business_name", "instagram", "tiktok", "has_vat", "has_experience", "reach", "presentation", "referral"]))}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-yellow-400 hover:text-yellow-400 text-sm px-4 py-2"><Download className="w-4 h-4" /> Esporta CSV</button>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#101010] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-zinc-400 text-xs uppercase tracking-widest">
                  <tr className="border-b border-white/5"><th className="p-4">Data</th><th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Telefono</th><th className="p-4">Tipo</th></tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-4 text-zinc-400 whitespace-nowrap text-xs">{new Date(a.created_at).toLocaleString("it-IT")}</td>
                      <td className="p-4 font-semibold">{a.first_name} {a.last_name}</td>
                      <td className="p-4 text-zinc-300">{a.email}</td>
                      <td className="p-4 text-zinc-300">{a.phone}</td>
                      <td className="p-4 text-zinc-400">{a.application_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "contacts" && (
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold tracking-tight">Contatti ({contacts.length})</h2>
              <button
                onClick={() => download("contatti.csv", toCSV(contacts, ["created_at", "name", "email", "phone", "reason", "bookmaker", "message"]))}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-yellow-400 hover:text-yellow-400 text-sm px-4 py-2"><Download className="w-4 h-4" /> Esporta CSV</button>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#101010] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-zinc-400 text-xs uppercase tracking-widest">
                  <tr className="border-b border-white/5"><th className="p-4">Data</th><th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Motivo</th><th className="p-4">Messaggio</th></tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b border-white/5">
                      <td className="p-4 text-zinc-400 whitespace-nowrap text-xs">{new Date(c.created_at).toLocaleString("it-IT")}</td>
                      <td className="p-4 font-semibold">{c.name}</td>
                      <td className="p-4 text-zinc-300">{c.email}</td>
                      <td className="p-4 text-zinc-400">{c.reason}</td>
                      <td className="p-4 text-zinc-300 max-w-xs truncate">{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "settings" && (
          <section className="max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight mb-6">Impostazioni</h2>
            <div className="grid gap-4">
              {[
                ["logo_url", "URL Logo"],
                ["telegram_bot_url", "Telegram Bot URL"],
                ["telegram_free_url", "Telegram gratuito URL"],
                ["telegram_vip_url", "Telegram VIP URL"],
                ["tiktok_url", "TikTok URL"],
                ["instagram_url", "Instagram URL"],
                ["whatsapp_url", "WhatsApp URL"],
                ["vinko_email", "Email Vinko"],
              ].map(([k, l]) => (
                <label key={k} className="text-xs text-zinc-400">{l}<input className={inputCls} value={set[k] || ""} onChange={(e) => setSet({ ...set, [k]: e.target.value })} /></label>
              ))}
              <div className="flex items-center gap-6">
                <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!set.telegram_vip_active} onChange={(e) => setSet({ ...set, telegram_vip_active: e.target.checked })} /> Attiva card Telegram VIP</label>
                <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!set.tiktok_active} onChange={(e) => setSet({ ...set, tiktok_active: e.target.checked })} /> Attiva card TikTok</label>
              </div>

              <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-5">
                <h3 className="font-semibold text-white mb-1">Notifiche candidature e contatti</h3>
                <p className="text-xs text-zinc-500 mb-4">Ogni volta che qualcuno invia il form Lavora con noi o Contatti, ricevi una notifica.</p>
                <div className="grid gap-4">
                  <label className="text-sm flex items-center gap-2">
                    <input type="checkbox" checked={!!set.notifications_email_active} onChange={(e) => setSet({ ...set, notifications_email_active: e.target.checked })} />
                    Attiva notifiche email
                  </label>
                  <label className="text-xs text-zinc-400">Email di destinazione
                    <input className={inputCls} value={set.notification_email || ""} onChange={(e) => setSet({ ...set, notification_email: e.target.value })} />
                  </label>

                  <div className="border-t border-white/5 pt-4">
                    <label className="text-sm flex items-center gap-2">
                      <input type="checkbox" checked={!!set.telegram_notifications_active} onChange={(e) => setSet({ ...set, telegram_notifications_active: e.target.checked })} />
                      Attiva notifiche Telegram
                    </label>
                    <p className="text-[11px] text-zinc-500 mt-2">Crea un bot su @BotFather, ottieni il token; scrivi a @userinfobot per il tuo Chat ID.</p>
                    <label className="text-xs text-zinc-400 mt-3 block">Bot Token
                      <input className={inputCls} value={set.telegram_notify_bot_token || ""} onChange={(e) => setSet({ ...set, telegram_notify_bot_token: e.target.value })} placeholder="123456:ABC-..." />
                    </label>
                    <label className="text-xs text-zinc-400 mt-3 block">Chat ID
                      <input className={inputCls} value={set.telegram_notify_chat_id || ""} onChange={(e) => setSet({ ...set, telegram_notify_chat_id: e.target.value })} placeholder="123456789" />
                    </label>
                  </div>
                </div>
              </div>

              <button onClick={saveSettings} className="mt-2 inline-flex items-center gap-2 rounded-full bg-yellow-400 text-black font-bold text-sm px-5 py-2.5 w-fit"><Save className="w-4 h-4" /> Salva impostazioni</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
